import { useEffect, useState } from "react";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Send, Eye, Trash2, Plus, Save } from "lucide-react";

const AUDIENCES = [
  { value: "newsletter", label: "Newsletter subscribers" },
  { value: "all_members", label: "All members" },
  { value: "explorer", label: "Explorer tier" },
  { value: "gold", label: "Gold tier" },
  { value: "black", label: "Black tier" },
  { value: "legend", label: "Legend tier" },
  { value: "customers", label: "All paying customers" },
  { value: "music_buyers", label: "Music buyers" },
  { value: "merch_buyers", label: "Merch buyers" },
];

export default function EmailCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("compose");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", subject: "", body: "", audience: "newsletter", audience_tag: "",
    scheduled_at: "", status: "draft",
  });
  const [preview, setPreview] = useState(null);
  const [editingTpl, setEditingTpl] = useState(null);

  const load = async () => {
    const [c, t] = await Promise.all([
      api.get("/email/campaigns"),
      api.get("/email/templates"),
    ]);
    setCampaigns(c.data || []);
    setTemplates(t.data || []);
  };

  useEffect(() => { load(); }, []);

  const applyTemplate = (tpl) => {
    setForm((f) => ({ ...f, subject: tpl.subject, body: tpl.body, name: f.name || tpl.name }));
    setTab("compose");
    toast.success(`Loaded template: ${tpl.name}`);
  };

  const saveCampaign = async (status = "draft") => {
    setBusy(true);
    try {
      const { data } = await api.post("/email/campaigns", { ...form, status });
      setForm({ name: "", subject: "", body: "", audience: "newsletter", audience_tag: "", scheduled_at: "", status: "draft" });
      toast.success(status === "sent" ? "Sending…" : "Saved");
      if (status === "sent") {
        await api.post(`/email/campaigns/${data.id}/send`);
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const previewCampaign = async (cid) => {
    const { data } = await api.get(`/email/campaigns/${cid}/preview`);
    setPreview(data);
  };

  const sendExisting = async (c) => {
    if (!window.confirm(`Send "${c.name}" to ${c.audience}?`)) return;
    await api.post(`/email/campaigns/${c.id}/send`);
    toast.success("Send queued");
    load();
  };

  const deleteCampaign = async (cid) => {
    if (!window.confirm("Delete this campaign?")) return;
    await api.delete(`/email/campaigns/${cid}`);
    load();
  };

  const saveTemplate = async (tpl) => {
    if (tpl.id) {
      await api.put(`/email/templates/${tpl.id}`, {
        slug: tpl.slug, name: tpl.name, subject: tpl.subject, body: tpl.body,
      });
    } else {
      await api.post("/email/templates", tpl);
    }
    toast.success("Template saved");
    setEditingTpl(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="email-campaigns-page">
      <h2 className="font-heading text-2xl text-white tracking-wider">Email Campaigns</h2>

      <div className="flex gap-1 border-b border-white/10">
        {[
          { k: "compose", label: "Compose" },
          { k: "campaigns", label: `Campaigns (${campaigns.length})` },
          { k: "templates", label: `Templates (${templates.length})` },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`email-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/40 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "compose" && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-[#0A0A0A] border border-white/5 rounded p-5 space-y-3">
            <input className="input-dark w-full" placeholder="Campaign name (internal)"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="campaign-name" />
            <input className="input-dark w-full" placeholder="Subject line"
                   value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                   data-testid="campaign-subject" />
            <div className="grid grid-cols-2 gap-3">
              <select className="input-dark" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="campaign-audience">
                {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
              <input className="input-dark" type="datetime-local" value={form.scheduled_at}
                     onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                     data-testid="campaign-schedule" />
            </div>
            <textarea className="input-dark w-full font-mono text-xs" rows={14}
                      placeholder="<h2>Your HTML content</h2><p>Merge tags: {{title}} {{link}} {{code}} {{percent}}</p>"
                      value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                      data-testid="campaign-body" />
            <div className="flex flex-wrap gap-2">
              <button disabled={busy} onClick={() => saveCampaign("draft")} data-testid="save-draft-btn"
                      className="text-xs px-4 py-2 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white uppercase tracking-widest">
                Save Draft
              </button>
              <button disabled={busy || !form.scheduled_at} onClick={() => saveCampaign("scheduled")} data-testid="schedule-btn"
                      className="text-xs px-4 py-2 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white uppercase tracking-widest disabled:opacity-40">
                Schedule
              </button>
              <button disabled={busy || !form.subject || !form.body} onClick={() => saveCampaign("sent")} data-testid="send-now-btn"
                      className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2 disabled:opacity-40">
                <Send className="w-3.5 h-3.5" /> Send Now
              </button>
            </div>
          </div>

          <div className="bg-[#0A0A0A] border border-white/5 rounded p-5">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Preview</p>
            <div className="border border-white/10 rounded p-4 bg-black">
              <p className="text-[10px] text-white/40 mb-2">Subject:</p>
              <p className="text-white text-sm mb-4">{form.subject || "—"}</p>
              <div className="text-white/70 text-xs" dangerouslySetInnerHTML={{ __html: form.body || "<em>Body preview…</em>" }} />
            </div>
          </div>
        </div>
      )}

      {tab === "campaigns" && (
        <div className="bg-[#0A0A0A] border border-white/5 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Subject</th>
                <th className="text-left py-3 px-4">Audience</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Sent</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr><td className="p-6 text-white/40" colSpan={6}>No campaigns yet.</td></tr>
              ) : campaigns.map((c) => (
                <tr key={c.id} className="border-b border-white/5" data-testid={`campaign-row-${c.id}`}>
                  <td className="py-3 px-4 text-white">{c.name}</td>
                  <td className="py-3 px-4 text-white/60">{c.subject}</td>
                  <td className="py-3 px-4 text-white/60 text-xs uppercase tracking-wider">{c.audience}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] uppercase tracking-widest ${
                      c.status === "sent" ? "text-green-400" : c.status === "scheduled" ? "text-yellow-400" : "text-white/40"
                    }`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-white/60">{c.stats?.sent || 0}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => previewCampaign(c.id)} className="text-white/40 hover:text-[#D4AF37]" data-testid={`preview-${c.id}`}><Eye className="w-4 h-4" /></button>
                      {c.status !== "sent" && (
                        <button onClick={() => sendExisting(c)} className="text-white/40 hover:text-[#D4AF37]" data-testid={`send-${c.id}`}><Send className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => deleteCampaign(c.id)} className="text-white/40 hover:text-red-400" data-testid={`delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview && (
            <div className="p-4 border-t border-white/10 bg-black">
              <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">Audience Preview</p>
              <p className="text-white text-sm">{preview.audience_count} recipients</p>
              <p className="text-white/40 text-xs mt-1">Sample: {(preview.sample || []).slice(0, 5).join(", ")}</p>
              <button onClick={() => setPreview(null)} className="mt-3 text-xs text-white/40 hover:text-white">Close</button>
            </div>
          )}
        </div>
      )}

      {tab === "templates" && (
        <div className="space-y-3">
          <button onClick={() => setEditingTpl({ slug: "", name: "", subject: "", body: "" })}
                  data-testid="new-template-btn"
                  className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
            <Plus className="w-3.5 h-3.5" /> New Template
          </button>
          <div className="grid md:grid-cols-2 gap-3">
            {templates.map((t) => (
              <div key={t.id} className="bg-[#0A0A0A] border border-white/5 rounded p-4" data-testid={`template-${t.slug}`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-white text-sm">{t.name}</div>
                    <div className="text-white/40 text-[11px]">{t.subject}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => applyTemplate(t)} className="text-[10px] text-[#D4AF37] hover:text-white uppercase tracking-widest">Use</button>
                    <button onClick={() => setEditingTpl(t)} className="text-[10px] text-white/50 hover:text-white uppercase tracking-widest">Edit</button>
                  </div>
                </div>
                <div className="text-white/40 text-[11px] line-clamp-2" dangerouslySetInnerHTML={{ __html: t.body.slice(0, 200) }} />
              </div>
            ))}
          </div>

          {editingTpl && (
            <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditingTpl(null)}>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 max-w-2xl w-full space-y-3" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-heading text-lg text-white">Edit Template</h3>
                <input className="input-dark w-full" placeholder="slug" value={editingTpl.slug} onChange={(e) => setEditingTpl({ ...editingTpl, slug: e.target.value })} />
                <input className="input-dark w-full" placeholder="Name" value={editingTpl.name} onChange={(e) => setEditingTpl({ ...editingTpl, name: e.target.value })} />
                <input className="input-dark w-full" placeholder="Subject" value={editingTpl.subject} onChange={(e) => setEditingTpl({ ...editingTpl, subject: e.target.value })} />
                <textarea className="input-dark w-full font-mono text-xs" rows={10} placeholder="HTML body" value={editingTpl.body} onChange={(e) => setEditingTpl({ ...editingTpl, body: e.target.value })} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingTpl(null)} className="text-xs px-4 py-2 text-white/50">Cancel</button>
                  <button onClick={() => saveTemplate(editingTpl)} data-testid="save-template-btn" className="text-xs bg-[#D4AF37] text-black px-4 py-2 hover:bg-white uppercase tracking-widest flex items-center gap-2">
                    <Save className="w-3.5 h-3.5" /> Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .input-dark { background:#000; border:1px solid rgba(255,255,255,0.1); color:#fff; padding:10px 12px; font-size:13px; border-radius:4px; outline:none; }
        .input-dark:focus { border-color:#D4AF37; }
        .line-clamp-2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
      `}</style>
    </div>
  );
}
